import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Dialog } from '@mui/material';
import styled from 'styled-components';
import { DashboardLayoutContainer } from 'components/Layouts/DashboardLayout';
import { deleteLandlordAction, getActiveLandlordAction } from 'store/actions/userActions';
import { LandlordTable } from 'components/Tables/LandlordTable';
import { failedNotif } from 'store/actions/notifActions';
import { DatePickerInput, Dropdown, colors, Button } from '@leapeasy/ui-kit';
import { GlobalUXState } from 'components/GlobalUXState';
import { DetailPageLoadingCenter } from 'components/DetailPageLoadingCenter';
import { useListQueryState } from 'hooks/useListQueryState';
import { useClampListPagination } from 'hooks/useClampListPagination';
import { getActiveLandlord } from 'store/sagas/APIs/user';
import { fetchAllPaginated } from 'utils/exportPagination';
import { useDropFilterActiveSummaryBar } from 'hooks/useDropFilterActiveSummaryBar';
import {
  buildOrgLandlordFilterChips,
  parseOrgLandlordFilterChipId,
} from 'utils/listTableActiveFilterChips';

// Images
import Img_1 from 'assets/img_1.png';

const parseLandlordFilters = (searchParams, _paramKey, fallback) => {
  const propertyCountParam = searchParams.get('propertyCount') || searchParams.get('buildings');
  return {
    selectedBuilings: propertyCountParam
      ? propertyCountParam.split(',').filter(Boolean)
      : fallback.selectedBuilings,
    selectedUnits: searchParams.get('units')
      ? searchParams.get('units').split(',').filter(Boolean)
      : fallback.selectedUnits,
    selectedInvitedDate: searchParams.get('invitedDate') || fallback.selectedInvitedDate || '',
  };
};

const serializeLandlordFilters = (filters) => ({
  propertyCount:
    Array.isArray(filters.selectedBuilings) && filters.selectedBuilings.length > 0
      ? filters.selectedBuilings.join(',')
      : '',
  units:
    Array.isArray(filters.selectedUnits) && filters.selectedUnits.length > 0
      ? filters.selectedUnits.join(',')
      : '',
  invitedDate: filters.selectedInvitedDate || '',
});

const areArraysEqual = (a = [], b = []) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

export const Landlord = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    filter,
    setFilter,
    sortOptions,
    setSortOptions,
    pagination,
    setPagination,
    extraFilters,
    setExtraFilters,
  } = useListQueryState({
    defaultFilter: {
      options: ['sf_landlord_id', 'email_address', 'name'],
      searchText: '',
      searchPlaceholder: 'Search Operator...',
    },
    extraFiltersParam: 'filters',
    defaultExtraFilters: {
      selectedBuilings: [],
      selectedUnits: [],
      selectedInvitedDate: '',
    },
    parseExtraFilters: parseLandlordFilters,
    serializeExtraFilters: serializeLandlordFilters,
  });

  // Dropdown options
  const [selectedBuilings, setSelectedBuildings] = useState([]);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [selectedInvitedDate, setSelectedInvitedDate] = useState('');
  const [selectedBuilingsDraft, setSelectedBuildingsDraft] = useState([]);
  const [selectedUnitsDraft, setSelectedUnitsDraft] = useState([]);
  const [selectedInvitedDateDraft, setSelectedInvitedDateDraft] = useState('');
  const [selectedLandlord, setSelectedLandlord] = useState(null);
  const landlords = useSelector((state) => state.getIn(['user', 'active_landlord']));
  const activeLandlordError = useSelector((state) =>
    state.getIn(['user', 'active_landlord_error']),
  );
  const [fullExportDatasetTotal, setFullExportDatasetTotal] = useState(undefined);
  const landlordTableRef = useRef(null);
  const [numOfBuildings, setNumOfBuildings] = useState([]);
  const [numOfUnits, setNumOfUnits] = useState([]);

  useEffect(() => {
    if (!extraFilters || typeof extraFilters !== 'object') return;
    const nextSelectedBuildings = Array.isArray(extraFilters.selectedBuilings)
      ? extraFilters.selectedBuilings
      : [];
    const nextSelectedUnits = Array.isArray(extraFilters.selectedUnits)
      ? extraFilters.selectedUnits
      : [];
    const nextSelectedInvitedDate = extraFilters.selectedInvitedDate || '';
    setSelectedBuildings((prev) =>
      areArraysEqual(prev, nextSelectedBuildings) ? prev : nextSelectedBuildings,
    );
    setSelectedUnits((prev) =>
      areArraysEqual(prev, nextSelectedUnits) ? prev : nextSelectedUnits,
    );
    setSelectedInvitedDate((prev) =>
      prev === nextSelectedInvitedDate ? prev : nextSelectedInvitedDate,
    );
    setSelectedBuildingsDraft((prev) =>
      areArraysEqual(prev, nextSelectedBuildings) ? prev : nextSelectedBuildings,
    );
    setSelectedUnitsDraft((prev) =>
      areArraysEqual(prev, nextSelectedUnits) ? prev : nextSelectedUnits,
    );
    setSelectedInvitedDateDraft((prev) =>
      prev === nextSelectedInvitedDate ? prev : nextSelectedInvitedDate,
    );
  }, [extraFilters]);

  const handleDispatch = useCallback(() => {
    dispatch(
      getActiveLandlordAction({
        filter: {
          ...filter,
          selectedBuilings,
          selectedUnits,
          selectedInvitedDate,
        },
        offset: pagination.pageNumber * pagination.rowsPerPage,
        limit: pagination.rowsPerPage,
        sortOptions,
      }),
    );
  }, [
    dispatch,
    filter,
    selectedBuilings,
    selectedUnits,
    selectedInvitedDate,
    pagination,
    sortOptions,
  ]);

  useEffect(() => {
    handleDispatch();
  }, [handleDispatch]);

  const onClickLandlord = useCallback(
    (ll) => {
      const landlordId = ll?.sf_landlord_id;
      if (!landlordId) {
        dispatch(failedNotif('Operator ID is missing.'));
        return;
      }
      navigate(`/user/operator/${landlordId}/detail/report`);
    },
    [dispatch, navigate],
  );

  const applyFunc = useCallback(() => {
    setSelectedBuildings(selectedBuilingsDraft);
    setSelectedUnits(selectedUnitsDraft);
    setSelectedInvitedDate(selectedInvitedDateDraft);
    setExtraFilters({
      selectedBuilings: selectedBuilingsDraft,
      selectedUnits: selectedUnitsDraft,
      selectedInvitedDate: selectedInvitedDateDraft,
    });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [
    selectedBuilingsDraft,
    selectedUnitsDraft,
    selectedInvitedDateDraft,
    setExtraFilters,
    setPagination,
  ]);

  const resetFunc = useCallback(() => {
    setSelectedBuildings([]);
    setSelectedUnits([]);
    setSelectedInvitedDate('');
    setSelectedBuildingsDraft([]);
    setSelectedUnitsDraft([]);
    setSelectedInvitedDateDraft('');
    setExtraFilters({
      selectedBuilings: [],
      selectedUnits: [],
      selectedInvitedDate: '',
    });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [setExtraFilters, setPagination]);

  const landlordsJSON = landlords && landlords.toJS();

  useClampListPagination({
    pagination,
    setPagination,
    totalItems: Number(landlordsJSON?.total) || 0,
    isLoading: Boolean(landlordsJSON?.isLoading),
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await getActiveLandlord({
          filter: {
            options: ['sf_landlord_id', 'email_address', 'name'],
            searchText: '',
            searchPlaceholder: 'Search Operator...',
            selectedBuilings: [],
            selectedUnits: [],
            selectedInvitedDate: '',
          },
          offset: 0,
          limit: 1,
          sortOptions,
        });
        if (!cancelled && response?.total != null) {
          setFullExportDatasetTotal(Number(response.total));
        }
      } catch (e) {
        // ignore prefetch failures
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sortOptions]);

  const handleRequestExportData = useCallback(async () => {
    const totalRecords = Number(landlordsJSON?.total) || 0;
    if (totalRecords === 0) return [];
    try {
      return await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getActiveLandlord({
            filter: {
              ...filter,
              selectedBuilings,
              selectedUnits,
              selectedInvitedDate,
            },
            offset,
            limit,
            sortOptions,
          }),
      });
    } catch (error) {
      throw new Error('Unable to export filtered operators right now.');
    }
  }, [
    landlordsJSON?.total,
    filter,
    selectedBuilings,
    selectedUnits,
    selectedInvitedDate,
    sortOptions,
  ]);

  const handleRequestFullExportData = useCallback(async () => {
    const totalRecords = Number(fullExportDatasetTotal) || Number(landlordsJSON?.total) || 0;
    if (totalRecords === 0) return [];
    try {
      return await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getActiveLandlord({
            filter: {
              options: ['sf_landlord_id', 'email_address', 'name'],
              searchText: '',
              searchPlaceholder: 'Search Operator...',
              selectedBuilings: [],
              selectedUnits: [],
              selectedInvitedDate: '',
            },
            offset,
            limit,
            sortOptions,
          }),
      });
    } catch (error) {
      throw new Error('Unable to export all operators right now.');
    }
  }, [fullExportDatasetTotal, landlordsJSON?.total, sortOptions]);

  const filterFunc = () => {
    if (!landlords) return;
    // List numbers of buildings
    let tmp_num_buildings = [];
    let tmp_num_units = [];
    for (let i = 0; i < landlordsJSON.data.length; i++) {
      tmp_num_buildings.filter(
        (building, _) => building.value === landlordsJSON.data[i].total_buildings,
      ).length === 0 &&
        tmp_num_buildings.push({
          text: landlordsJSON.data[i].total_buildings,
          value: landlordsJSON.data[i].total_buildings,
        });

      tmp_num_units.filter((building, _) => building.value === landlordsJSON.data[i].total_units)
        .length === 0 &&
        tmp_num_units.push({
          text: landlordsJSON.data[i].total_units,
          value: landlordsJSON.data[i].total_units,
        });
    }
    tmp_num_buildings.sort(function (a, b) {
      return a.value - b.value;
    });
    tmp_num_units.sort(function (a, b) {
      return a.value - b.value;
    });
    setNumOfBuildings([...tmp_num_buildings]);
    setNumOfUnits([...tmp_num_units]);
  };

  useEffect(() => {
    filterFunc();
  }, [landlords]);

  const handleDelete = () => {
    if (!selectedLandlord) return;
    dispatch(deleteLandlordAction(selectedLandlord));
    setSelectedLandlord(null);
  };

  const listFilterChips = useMemo(
    () =>
      buildOrgLandlordFilterChips({
        selectedBuilings,
        selectedUnits,
        selectedInvitedDate,
      }),
    [selectedBuilings, selectedUnits, selectedInvitedDate],
  );

  const handleRemoveOrgLandlordFilterChip = useCallback(
    (chipId) => {
      const parsed = parseOrgLandlordFilterChipId(chipId);
      if (!parsed) return;
      const bumpPage = () =>
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
      if (parsed.type === 'invitedDate') {
        setSelectedInvitedDate('');
        setSelectedInvitedDateDraft('');
        setExtraFilters((prev) => ({ ...prev, selectedInvitedDate: '' }));
        bumpPage();
        return;
      }
      if (parsed.type === 'propertyCount') {
        const next = selectedBuilings.filter((s) => s !== parsed.value);
        setSelectedBuildings(next);
        setSelectedBuildingsDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedBuilings: next }));
        bumpPage();
        return;
      }
      if (parsed.type === 'units') {
        const next = selectedUnits.filter((s) => s !== parsed.value);
        setSelectedUnits(next);
        setSelectedUnitsDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedUnits: next }));
        bumpPage();
      }
    },
    [selectedBuilings, selectedUnits, setExtraFilters, setPagination],
  );

  const { dropFilterActiveSummary, dropFilterActiveCount } = useDropFilterActiveSummaryBar(
    listFilterChips,
    handleRemoveOrgLandlordFilterChip,
    resetFunc,
  );

  return (
    <DashboardLayoutContainer>
      {activeLandlordError ? (
        <GlobalUXState variant="apiError" actionLabel="Retry" onAction={handleDispatch} />
      ) : landlords ? (
        <Box>
          <Dialog open={!!selectedLandlord}>
            <DialogContainer col1={colors.purple[900]} col2={colors.neutral[900]}>
              <img src={Img_1} />
              <p>Are you sure you want to delete this operator?</p>
              <div>
                <Button
                  size="medium"
                  variant="tertiary"
                  style={{ flexShrink: 2 }}
                  onClick={() => setSelectedLandlord(null)}
                >
                  Discard
                </Button>

                <Button
                  size="medium"
                  variant="primary"
                  style={{ flexShrink: 2 }}
                  onClick={handleDelete}
                >
                  Yes, Delete
                </Button>
              </div>
            </DialogContainer>
          </Dialog>
          <LandlordTable
            ref={landlordTableRef}
            landlords={landlordsJSON.data}
            filter={filter}
            setSelectedLandlord={setSelectedLandlord}
            dropFilterActiveSummary={dropFilterActiveSummary}
            dropFilterActiveCount={dropFilterActiveCount}
            dropFilter={{
              component: (
                <DropFilterContainer>
                  <Dropdown
                    onChange={(e) => {
                      setSelectedBuildingsDraft(e.target.value);
                    }}
                    options={numOfBuildings}
                    value={selectedBuilingsDraft}
                    multiselect={true}
                    showSearch={false}
                    placeholder=""
                    placeholderFlag={false}
                    label="Property Count"
                    style={{
                      width: '100%',
                    }}
                  />
                  <Dropdown
                    onChange={(e) => {
                      setSelectedUnitsDraft(e.target.value);
                    }}
                    options={numOfUnits}
                    value={selectedUnitsDraft}
                    multiselect={true}
                    showSearch={false}
                    placeholder=""
                    placeholderFlag={false}
                    label="Unit Count"
                    style={{
                      width: '100%',
                    }}
                  />

                  <RangeDatePickerContainer>
                    <DatePickerInput
                      label={'Invited Date'}
                      size="small"
                      value={selectedInvitedDateDraft}
                      onChange={(date) => {
                        setSelectedInvitedDateDraft(date.target.value);
                      }}
                    />
                  </RangeDatePickerContainer>
                </DropFilterContainer>
              ),
              applyFunc: applyFunc,
              resetFunc: resetFunc,
            }}
            onChangeFilter={setFilter}
            pagination={{
              ...pagination,
              totalItems: landlordsJSON.total || landlordsJSON.data.length,
              totalPages: Math.ceil(
                (landlordsJSON.total || landlordsJSON.data.length) / pagination.rowsPerPage,
              ),
            }}
            onChangePagination={setPagination}
            sortOptions={sortOptions}
            onChangeSort={setSortOptions}
            onClickLandlord={onClickLandlord}
            onRequestExportData={handleRequestExportData}
            onRequestFullExportData={handleRequestFullExportData}
            exportFullDatasetTotal={fullExportDatasetTotal}
          />
        </Box>
      ) : (
        <DetailPageLoadingCenter label="Loading operators..." />
      )}
    </DashboardLayoutContainer>
  );
};

const DropFilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  * {
    box-sizing: border-box;
  }
`;

const RangeDatePickerContainer = styled.div`
  display: flex;
  max-width: 100%;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  width: 100%;
  * {
    box-sizing: border-box;
  }
  & > div {
    width: 100% !important;
  }

  .ui-kit-input-body {
    width: 278px;
  }
`;

const DialogContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  border-radius: 8px;
  width: 350px;
  text-align: center;
  box-sizing: border-box;
  * {
    box-sizing: border-box;
  }
  & > p:nth-of-type(1) {
    color: ${(props) => props.col1};
    font-size: 14px;
    font-weight: 600;
  }

  & > p:nth-of-type(2) {
    color: ${(props) => props.col2};
    font-size: 12px;
    font-weight: 400;
  }

  & > div {
    margin-top: 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    width: 100%;

    button {
      flex: 1;
    }
  }
`;
